import { useCallback } from "react";
import { Bar, BarChart, CartesianGrid, LabelList, XAxis } from "recharts";
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from "@/components/ui/chart";
import { Filter } from "@/v2/entities/explore/Explore.filter";
import { ExploreInteractionType } from "@/v2/entities/explore/Explore.model";
import {
  CHART_UNCLASSIFIED_LABEL,
  ExploreChartLoader,
  ExploreChartEmptyState,
} from "./explore-widgets-utils";
import { useExploreChart } from "./useExploreChart";
import { useSize } from "@/v2/shared/hooks/useSize";
import { TruncatedTick } from "./TruncatedTick";

export const barChartMargin = { left: 8, right: 8, top: 8, bottom: 8 };

export function useSlotWidth(dataLength: number, width?: number) {
  const innerWidth = Math.max(
    0,
    (width ?? 0) - barChartMargin.left - barChartMargin.right
  );
  const slotWidth = dataLength > 0 ? innerWidth / dataLength : 0;

  return slotWidth;
}

interface ExploreBarChartProps {
  source?: ExploreInteractionType;
  groupBy?: string;
  filter?: Filter;
}

export function ExploreBarChart({
  source,
  groupBy,
  filter,
}: ExploreBarChartProps) {
  const { ref, width } = useSize<HTMLDivElement>();

  const { items, isLoading, chartConfig, itemToLabelMap, labelFormatter } =
    useExploreChart(source, groupBy, filter);

  const slotWidth = useSlotWidth(items.length, width);

  const xTickLabelFormatter = useCallback(
    (value: string) => {
      if (labelFormatter == null) {
        return value || CHART_UNCLASSIFIED_LABEL;
      }

      const label = itemToLabelMap.get(value);
      return labelFormatter(label) || CHART_UNCLASSIFIED_LABEL;
    },
    [itemToLabelMap, labelFormatter]
  );

  if (isLoading) {
    return <ExploreChartLoader />;
  }

  if (items.length === 0) {
    return <ExploreChartEmptyState variant="no-data" />;
  }

  return (
    <ChartContainer
      config={chartConfig}
      className="aspect-auto h-full w-full"
      ref={ref}
    >
      <BarChart accessibilityLayer data={items} margin={barChartMargin}>
        <CartesianGrid vertical={false} />
        <XAxis
          dataKey={"value"}
          interval={0}
          minTickGap={6}
          tickLine={false}
          axisLine={false}
          height={48}
          tick={
            <TruncatedTick
              axis="x"
              anchor="middle"
              dy={8}
              slotWidth={slotWidth}
              pad={4}
              labelFormatter={xTickLabelFormatter}
            />
          }
        />
        <ChartTooltip content={<ChartTooltipContent indicator="line" />} />
        <Bar
          dataKey="volume"
          fill="var(--color-volume)"
          radius={8}
          maxBarSize={60}
        >
          <LabelList
            position="top"
            offset={12}
            className="font-mono font-medium tabular-nums text-foreground fill-foreground"
            fontSize={12}
            formatter={(value: number) => value.toLocaleString("en-US")}
          />
        </Bar>
      </BarChart>
    </ChartContainer>
  );
}
